import { useState, useEffect } from "react";
import Tabs from "./tabs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Grid, TextField, Card, CardContent, IconButton, InputAdornment, Box, Button, CircularProgress, Stack } from "@mui/material";
import { FieldSelect } from "../../../components/selections/fieldInputs";
import FilterMultiSelect from "../../../components/selections/filterMultiSelect";
import { addEmployeeThunk, getEmployeeThunk, employeeLevelsThunk, employeeModulesThunk } from "../employeeThunk";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { APP_ROUTES } from "../../../config/appRoutes";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { stepCardSx, stepActionsSx, nextBtnSx, backBtnSx, StepHeader } from "../../campaign/components/stepUi";
import { EmpLabel, empInputSx, FormSection } from "./employeeUi";

// New-employee form. `embedded` = inside the list page popup (no page header, Cancel button,
// onSuccess(message) closes the popup and the list shows the message).
export function EmployeeCreateForm({ embedded = false, onCancel, onSuccess }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { branches, departments, branchDepartments } = useSelector((state) => state.general);
    const { employeeLevels, employeeModules, status } = useSelector((state) => state.employees);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [state, setState] = useState({
        employeeType: 1,
        selectedDepartment: [],
        selectedBranch: [],
        selectBranchDepartments: [],
        employeeName: "",
        userName: "",
        empPassword: "",
        empEmailAdd: "",
        empContact: "",
        selectedModules: [],
        errors: {},
        showPassword: false,
    });



    useEffect(() => {
        dispatch(employeeLevelsThunk());
        dispatch(employeeModulesThunk());
    }, [dispatch]);

    const { employeeType, selectedDepartment, selectedBranch, selectBranchDepartments, employeeName, userName, empPassword, empEmailAdd, empContact, selectedModules, showPassword } = state;
 
    const addEmpFileds = [
        { id: 1, value: employeeType, type: "select", options: employeeLevels.map((item) => ({ value: item.id, label: item.user_level })), label: "Employee type", name: "employeeType" },
        { id: 2, value: selectedDepartment, type: "multi-select", options: departments.map((item) => ({ value: item.id, label: item.department_name })), label: "Select Department", name: "selectedDepartment" },
        { id: 3, value: selectedBranch, type: "multi-select", options: branches.map((item) => ({ value: item.id, label: item.branch_name })), label: "Select Branch", name: "selectedBranch" },
        { id: 4, value: selectBranchDepartments, type: "multi-select", options: branchDepartments.map((item) => ({ value: item.id, label: item.branch_department_name })), label: "Select Branch Departments", name: "selectBranchDepartments" },
        { id: 5, value: employeeName, type: "text", label: "Name", name: "employeeName" },
        { id: 6, value: userName, type: "text", label: "User Name", name: "userName" },
        { id: 7, value: empPassword, type: "text", label: "Password", name: "empPassword" },
        { id: 8, value: empEmailAdd, type: "email", label: "Email", name: "empEmailAdd" },
        { id: 9, value: empContact, type: "text", label: "Mobile Number [6-9]", name: "empContact" },
        { id: 10, value: selectedModules, type: "multi-select", options: employeeModules.map((item) => ({ value: item.id, label: item.display_name, access_name: item.name  })), label: "Select Modules", name: "selectedModules" },
    ];



    const handleChange = (name, value) => {
        let newValue = value;

        if (name === "empContact") {
            newValue = value.replace(/\D/g, "").slice(0, 10);
        } else if (name === "userName") {
            // Allow spaces, but not at start/end, and collapse multiple spaces
            newValue = value.replace(/\s/g, "").slice(0, 20);
        } else if (name === "empEmailAdd") {
            // Disallow any spaces in email
            newValue = value.replace(/\s/g, "").slice(0, 100);
        } else if (name === "employeeName") {
            // Allow spaces, but not at start/end, and collapse multiple spaces
            newValue = value.replace(/^\s+/, "").replace(/\s{2,}/g, " ").slice(0, 50);
        } else if (name === "empPassword") {
            // Remove all spaces
            newValue = value.replace(/\s/g, "").slice(0, 16);
        }

        setState((prev) => ({
            ...prev,
            [name]: newValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    const handleMultiSelectChange = (name, selectedValues) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValues,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    const handleSingleSelectChange = (name, selectedValue) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        const validations = [
            { valid: !!employeeType, message: "Employee type is required", field: "employeeType" },

            ...(employeeType === 2
                ? [{ valid: selectedDepartment.length > 0, message: "Select at least one department", field: "selectedDepartment" }]
                : []),

            ...(employeeType === 3
                ? [{ valid: selectedBranch.length > 0, message: "Select at least one branch", field: "selectedBranch" }]
                : []),

            ...(employeeType === 4
                ? [{ valid: selectBranchDepartments.length > 0, message: "Select branch departments", field: "selectBranchDepartments" }]
                : []),

            { valid: !!employeeName, message: "Name is required", field: "employeeName" },
            { valid: !!userName, message: "User Name is required", field: "userName" },
            { valid: !!empPassword, message: "Password is required", field: "empPassword" },
            { valid: empPassword?.length >= 6 && empPassword?.length <= 16, message: "Password must be 6–16 characters", field: "empPassword" },
            { valid: !!empEmailAdd, message: "Email is required", field: "empEmailAdd" },
            { valid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(empEmailAdd), message: "Enter a valid email address", field: "empEmailAdd" },
            { valid: !!empContact, message: "Mobile number is required", field: "empContact" },
            { valid: /^[6-9]\d{9}$/.test(empContact), message: "Enter a valid 10-digit mobile number", field: "empContact" },
            { valid: selectedModules.length > 0, message: "Select at least one module", field: "selectedModules" },
        ];

        for (let rule of validations) {
            if (!rule.valid) {
                setSnackbar({
                    open: true,
                    message: rule.message,
                    severity: "error",
                });

                setState((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, [rule.field]: rule.message },
                }));
                return;
            }
        }

        try {
            const cleanedEmail = empEmailAdd.replace(/\s+/g, "");
            const result = await dispatch(
                addEmployeeThunk({
                    credentials: {
                        "employee_name": employeeName,
                        "emp_mobile": empContact,
                        "emp_email": cleanedEmail,
                        "username": userName,
                        "password": empPassword,
                        "employee_type_id": employeeType,
                        "department_id": selectedDepartment.length > 0 ? selectedDepartment.map((item) => item.value).toString() : "",
                        "branch_id": selectedBranch.length > 0 ? selectedBranch.map((item) => item.value).toString() : "",
                        "branch_department_id": selectBranchDepartments.length > 0 ? selectBranchDepartments.map((item) => item.value).toString() : "",
                        "module_access": selectedModules.reduce((acc, item) => {
                            acc[item.access_name] = true;
                            return acc;
                        }, {}) || "",
                    }
                })
            );

            if (result.meta.requestStatus === "fulfilled") {
                if (onSuccess) {
                    dispatch(getEmployeeThunk({ type: "getEmpData" }));
                    onSuccess(result.payload?.message);
                    return;
                }
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });

                dispatch(getEmployeeThunk({ type: "getEmpData" }));
                navigate(APP_ROUTES.EMPLOYEE.LIST);
            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    }

    const handleClickShowPassword = () => {
        setState((prevState) => ({
            ...prevState,
            showPassword: !prevState.showPassword,
        }));
    };

    // Field groups (layout only) — each group renders the same fields with the same visibility rules
    const accountFieldNames = ["employeeName", "userName", "empPassword", "empEmailAdd", "empContact"];
    const accessFieldNames = ["employeeType", "selectedDepartment", "selectedBranch", "selectBranchDepartments", "selectedModules"];

    // Placeholders per field (display only)
    const placeholders = {
        employeeName: "e.g. Ravi Kumar",
        userName: "Login user name",
        empPassword: "6–16 characters",
        empEmailAdd: "name@company.com",
        empContact: "10-digit mobile number",
    };

    const renderField = (field) => {
        if (employeeType !== null) {
            if (field.name === "selectedDepartment" && employeeType !== 2) return null;
            if (field.name === "selectedBranch" && employeeType !== 3) return null;
            if (field.name === "selectBranchDepartments" && employeeType !== 4) return null;

            if (
                employeeType === 1 &&
                ["selectedDepartment", "selectedBranch", "selectBranchDepartments"].includes(field.name)
            ) {
                return null;
            }
        }
        return (
            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={field.id}>
                {field.type === "text" || field.type === "email" || field.type === "number" ? (
                    <Box>
                        <EmpLabel label={field.label} />
                        <TextField
                            fullWidth
                            size="small"
                            type={field.id === 7 ? (showPassword ? "text" : "password") : field.type}
                            placeholder={placeholders[field.name]}
                            value={field.value}
                            onChange={(e) => handleChange(field.name, e.target.value)}
                            error={!!state.errors[field.name]}
                            sx={empInputSx}
                            inputProps={{
                                maxLength:
                                    field.name === "userName" ? 20 :
                                        field.name === "employeeName" ? 50 :
                                            field.name === "empEmailAdd" ? 100 :
                                                field.name === "empContact" ? 10 :
                                                    field.id === 7 ? 16 : undefined,
                                minLength: field.id === 7 ? 6 : field.id === 9 ? 10 : undefined,
                                inputMode: field.id === 9 ? "numeric" : undefined,
                                pattern: field.id === 9 ? "[0-9]*" : undefined,
                            }}
                            onKeyDown={field.name === "empEmailAdd" ? (e) => {
                                if (e.key === " " || e.key === "Spacebar") {
                                    e.preventDefault();
                                }
                            } : undefined}
                            InputProps={
                                field.id === 7 ? {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClickShowPassword} size="small" sx={{
                                                cursor: "pointer",
                                                fontSize: "12px"
                                            }}>
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                } : {}}
                        />
                    </Box>
                ) : field.type === "select" ? (
                    <FieldSelect
                        name={field.name}
                        label={field.label}
                        options={field.options}
                        value={field.value}
                        onChange={handleSingleSelectChange}
                        error={!!state.errors[field.name]}
                        placeholder="Choose employee type"
                    />
                ) : field.type === "multi-select" ? (
                    <FilterMultiSelect
                        name={field.name}
                        label={field.label}
                        options={field.options}
                        value={state[field.name] || []}
                        onChange={handleMultiSelectChange}
                        formHelperText={false}
                        error={!!state.errors[field.name]}
                    />
                ) : null}
            </Grid>
        )
    };

    const Wrap = embedded ? Box : Tabs;

    return (
        <div>
            <Wrap>
                <Card elevation={0} sx={embedded ? { borderRadius: 0, boxShadow: "none", overflow: "visible" } : { ...stepCardSx, mt: 1 }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        {!embedded && (
                            <StepHeader
                                icon={<PersonAddAlt1Icon sx={{ fontSize: 20 }} />}
                                title="New Employee"
                                subtitle="Add login details, then choose the role and modules they can access"
                            />
                        )}

                        {embedded ? (
                            // popup: no "Account details" heading, fields start straight away
                            <Grid container spacing={2.5}>
                                {addEmpFileds.filter((f) => accountFieldNames.includes(f.name)).map(renderField)}
                            </Grid>
                        ) : (
                            <FormSection first icon={<PersonOutlineIcon />} title="Account details" subtitle="Name, login and contact information">
                                <Grid container spacing={2.5}>
                                    {addEmpFileds.filter((f) => accountFieldNames.includes(f.name)).map(renderField)}
                                </Grid>
                            </FormSection>
                        )}

                        <FormSection icon={<AdminPanelSettingsIcon />} title="Role & access" subtitle="Employee type, scope and module access" color="#2563eb">
                            <Grid container spacing={2.5}>
                                {addEmpFileds.filter((f) => accessFieldNames.includes(f.name)).map(renderField)}
                            </Grid>
                        </FormSection>
                    </CardContent>

                    <Box sx={stepActionsSx}>
                        <Stack direction="row" spacing={1.5}>
                        {embedded && (
                            <Button onClick={onCancel} sx={backBtnSx}>Cancel</Button>
                        )}
                        <Button
                            onClick={status === "loading" ? null : handleSubmit}
                            disabled={status === "loading"}
                            startIcon={status === "loading" ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : <PersonAddAlt1Icon />}
                            sx={nextBtnSx}
                        >
                            {status === "loading" ? "Submitting.." : "Create Employee"}
                        </Button>
                        </Stack>
                    </Box>

                    <SnackbarMessage
                        open={snackbar.open}
                        message={snackbar.message}
                        severity={snackbar.severity}
                        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    />
                </Card>
            </Wrap>
        </div>
    );
}

// /employee-creation page
export default function EmployeeCreation() {
    return <EmployeeCreateForm />;
}
